import { useEffect, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Checkbox,
    Group,
    Modal,
    NumberInput,
    Pagination,
    SegmentedControl,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power, Wand2 } from 'lucide-react';
import { api } from '@/api/client';
import { useProducts } from '@/api/products';
import type { Paged, PromoCode } from '@/api/types';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { formatDate, isoToLocalInput, localInputToIso } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';

const CODE_RE = /^[A-Z0-9]{3,64}$/;
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // без 0/O/1/I — их путают при вводе

function generateCode() {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('');
}

function fmtDiscount(p: PromoCode) {
    return p.discount_type === 'percent' ? `${Number(p.discount_value)}%` : `${Number(p.discount_value)} ₽`;
}

function fmtTerm(p: PromoCode) {
    const from = p.valid_from ? formatDate(p.valid_from) : null;
    const until = p.valid_until ? formatDate(p.valid_until) : null;
    if (from && until) return `${from} – ${until}`;
    if (until) return `до ${until}`;
    if (from) return `с ${from}`;
    return 'бессрочно';
}

interface FormState {
    code: string;
    discount_type: 'percent' | 'fixed';
    discount_value: number | string;
    product_id: string | null;
    user_id: number | string;
    max_uses: number | string;
    valid_from: string;
    valid_until: string;
    active: boolean;
    new_users_only: boolean;
}

function toForm(p: PromoCode | null): FormState {
    return {
        code: p?.code ?? '',
        discount_type: p?.discount_type ?? 'percent',
        discount_value: p ? Number(p.discount_value) : 50,
        product_id: p?.product_id ? String(p.product_id) : null,
        user_id: p?.user_id ?? '',
        max_uses: p?.max_uses ?? 1,
        // Новый код по умолчанию действует с текущего момента — так было и в старой админке.
        valid_from: p ? isoToLocalInput(p.valid_from) : isoToLocalInput(new Date().toISOString()),
        valid_until: p ? isoToLocalInput(p.valid_until) : '',
        active: p ? !!p.active : true,
        new_users_only: !!p?.new_users_only,
    };
}

function CodeAvailability({ code, original }: { code: string; original: string }) {
    const [debounced] = useDebouncedValue(code, 400);
    const enabled = CODE_RE.test(debounced) && debounced !== original;
    const check = useQuery({
        queryKey: ['promo-check', debounced],
        queryFn: () =>
            api.get<{ available: boolean }>(`/api/admin/promo-codes/check?code=${encodeURIComponent(debounced)}`),
        enabled,
    });
    if (!code) return <>Пусто — код сгенерируется автоматически</>;
    if (code === original) return <>Текущий код</>;
    if (code.length < 3) return <Text span c="red" inherit>Минимум 3 символа</Text>;
    if (!enabled || check.isFetching) return <>Проверяем…</>;
    if (check.data?.available) return <Text span c="brand" inherit>✓ свободен</Text>;
    return <Text span c="red" inherit>✗ уже занят</Text>;
}

function PromoModal({ promo, opened, onClose }: { promo: PromoCode | null; opened: boolean; onClose: () => void }) {
    const queryClient = useQueryClient();
    const products = useProducts();
    const [form, setForm] = useState<FormState>(toForm(promo));
    const fullScreen = useMediaQuery('(max-width: 48em)');

    useEffect(() => {
        if (opened) setForm(toForm(promo));
    }, [opened, promo]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

    const save = useMutation({
        mutationFn: () => {
            const value = Number(form.discount_value);
            if (!value || value <= 0) throw new Error('Укажите размер скидки');
            if (form.discount_type === 'percent' && value > 100) throw new Error('Процент не может быть больше 100');
            if (form.code && !CODE_RE.test(form.code)) throw new Error('Код: 3–64 символа, латиница и цифры');
            const body = {
                code: form.code || undefined,
                discount_type: form.discount_type,
                discount_value: value,
                product_id: form.product_id ? Number(form.product_id) : null,
                user_id: form.user_id ? Number(form.user_id) : null,
                max_uses: Number(form.max_uses) || 1,
                valid_from: localInputToIso(form.valid_from),
                valid_until: localInputToIso(form.valid_until),
                active: form.active,
                new_users_only: form.new_users_only,
            };
            return promo ? api.put(`/api/admin/promo-codes/${promo.id}`, body) : api.post('/api/admin/promo-codes', body);
        },
        onSuccess: () => {
            notifySuccess('Промокод сохранён');
            queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
            onClose();
        },
        onError: notifyError,
    });

    return (
        <Modal opened={opened} onClose={onClose} title={promo ? 'Промокод' : 'Новый промокод'} size="lg" fullScreen={fullScreen}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save.mutate();
                }}
            >
                <Stack>
                    <TextInput
                        label="Код"
                        placeholder="Оставьте пустым для автогенерации"
                        value={form.code}
                        onChange={(e) => set('code', e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        maxLength={64}
                        styles={{ input: { fontFamily: 'monospace', letterSpacing: 1 } }}
                        description={<CodeAvailability code={form.code} original={promo?.code ?? ''} />}
                        rightSectionWidth={44}
                        rightSection={
                            <Tooltip label="Сгенерировать">
                                <ActionIcon variant="subtle" onClick={() => set('code', generateCode())} aria-label="Сгенерировать">
                                    <Wand2 size={16} />
                                </ActionIcon>
                            </Tooltip>
                        }
                    />
                    <SimpleGrid cols={2}>
                        <SegmentedControl
                            data={[
                                { value: 'percent', label: 'Процент' },
                                { value: 'fixed', label: 'Рубли' },
                            ]}
                            value={form.discount_type}
                            onChange={(v) => set('discount_type', v as FormState['discount_type'])}
                            style={{ alignSelf: 'end' }}
                        />
                        <NumberInput
                            label={form.discount_type === 'percent' ? 'Скидка, %' : 'Скидка, ₽'}
                            min={0.01}
                            max={form.discount_type === 'percent' ? 100 : undefined}
                            decimalScale={2}
                            value={form.discount_value}
                            onChange={(v) => set('discount_value', v)}
                            required
                        />
                    </SimpleGrid>
                    <Select
                        label="Услуга"
                        placeholder="Все услуги"
                        clearable
                        data={(products.data ?? []).map((p) => ({ value: String(p.id), label: `${p.name} (${p.price_rub} ₽)` }))}
                        value={form.product_id}
                        onChange={(v) => set('product_id', v)}
                    />
                    <SimpleGrid cols={2}>
                        <NumberInput
                            label="ID пользователя"
                            placeholder="Пусто — общий"
                            min={1}
                            allowDecimal={false}
                            value={form.user_id}
                            onChange={(v) => set('user_id', v)}
                        />
                        <NumberInput
                            label="Лимит применений"
                            min={1}
                            allowDecimal={false}
                            value={form.max_uses}
                            onChange={(v) => set('max_uses', v)}
                        />
                    </SimpleGrid>
                    <SimpleGrid cols={{ base: 1, xs: 2 }}>
                        <TextInput
                            type="datetime-local"
                            label="Действует с"
                            value={form.valid_from}
                            onChange={(e) => set('valid_from', e.currentTarget.value)}
                        />
                        <TextInput
                            type="datetime-local"
                            label="Действует до"
                            value={form.valid_until}
                            onChange={(e) => set('valid_until', e.currentTarget.value)}
                        />
                    </SimpleGrid>
                    <Checkbox label="Активен" checked={form.active} onChange={(e) => set('active', e.currentTarget.checked)} />
                    <Checkbox
                        label="Только для новых пользователей"
                        description="Сработает лишь на первом заказе клиента (нет других не-отменённых заказов)"
                        checked={form.new_users_only}
                        onChange={(e) => set('new_users_only', e.currentTarget.checked)}
                    />
                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button type="submit" loading={save.isPending}>
                            Сохранить
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}

export function PromoScreen() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState<PromoCode | null>(null);
    const [opened, setOpened] = useState(false);

    const list = useQuery({
        queryKey: ['promo-codes', page],
        queryFn: () => api.get<Paged & { promo_codes: PromoCode[] }>(`/api/admin/promo-codes?page=${page}`),
        placeholderData: keepPreviousData,
    });

    const toggle = useMutation({
        mutationFn: (p: PromoCode) => api.put(`/api/admin/promo-codes/${p.id}`, { active: !p.active }),
        onSuccess: (_, p) => {
            notifySuccess(p.active ? 'Промокод выключен' : 'Промокод включён');
            queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
        },
        onError: notifyError,
    });

    const open = (p: PromoCode | null) => {
        setEditing(p);
        setOpened(true);
    };

    const columns: Column<PromoCode>[] = [
        {
            key: 'code',
            title: 'Код',
            mobile: 'title',
            render: (p) => (
                <Group gap="xs" wrap="nowrap">
                    <Text ff="monospace" fw={700} inherit>
                        {p.code}
                    </Text>
                    {p.referrer_user_id && (
                        <Badge size="xs" color="grape">
                            реф
                        </Badge>
                    )}
                </Group>
            ),
        },
        { key: 'discount', title: 'Скидка', render: fmtDiscount },
        { key: 'product', title: 'Услуга', render: (p) => (p.product_id ? p.product_name || `#${p.product_id}` : 'Все') },
        {
            key: 'whom',
            title: 'Кому',
            render: (p) => (p.user_id ? (p.user_login ? `@${p.user_login}` : p.user_name || `id ${p.user_id}`) : 'Общий'),
        },
        { key: 'uses', title: 'Исп. / лимит', render: (p) => `${p.uses_count ?? 0} / ${p.max_uses}` },
        { key: 'term', title: 'Срок', render: fmtTerm },
        {
            key: 'status',
            title: 'Статус',
            render: (p) => <Badge color={p.active ? 'brand' : 'gray'}>{p.active ? 'Активен' : 'Выключен'}</Badge>,
        },
    ];

    return (
        <Page
            title="Промокоды"
            actions={
                <Button leftSection={<Plus size={16} />} onClick={() => open(null)}>
                    Добавить
                </Button>
            }
        >
            <QueryState isLoading={list.isLoading} error={list.error} onRetry={list.refetch}>
                <ResponsiveTable
                    data={list.data?.promo_codes ?? []}
                    columns={columns}
                    rowKey={(p) => p.id}
                    onRowClick={open}
                    empty="Промокодов нет"
                    actions={(p) => (
                        <>
                            <Button size="compact-sm" variant="light" leftSection={<Pencil size={14} />} onClick={() => open(p)}>
                                Изменить
                            </Button>
                            <Button
                                size="compact-sm"
                                variant="light"
                                color={p.active ? 'red' : 'brand'}
                                leftSection={<Power size={14} />}
                                loading={toggle.isPending && toggle.variables?.id === p.id}
                                onClick={() => toggle.mutate(p)}
                            >
                                {p.active ? 'Выкл' : 'Вкл'}
                            </Button>
                        </>
                    )}
                />
                {(list.data?.pages ?? 1) > 1 && (
                    <Group justify="center">
                        <Pagination total={list.data!.pages} value={page} onChange={setPage} />
                    </Group>
                )}
            </QueryState>
            <PromoModal promo={editing} opened={opened} onClose={() => setOpened(false)} />
        </Page>
    );
}
